import React, { useState } from 'react';
import Slider, { RangeSlider } from '@ajat/design-system/Slider';
import { ComponentPage, DemoCard } from '../shared';

/**
 * Slider has no Figma node yet — it was designed in code for the CLS-DK logo
 * generator — so this page carries no `figmaNode` chip and the component is
 * deliberately absent from COMPONENTS_CONFIG.
 */

export const meta = {
  title: 'Slider',
  group: 'Forms',
  importPath: '@ajat/design-system/Slider',
  description:
    'Labelled range control — label left, formatted value right, filled track below. It wraps a native <input type="range">, so keyboard stepping (arrows / Home / End) and form semantics come for free. Used for "Logo størrelse" on the CLS-DK logo generator. RangeSlider is the two-thumb version (the Filter price range): two overlaid native inputs on one track, filled between the thumbs.',
  propSets: [
    {
      title: 'Slider',
      component: Slider,
      componentName: 'Slider',
      rows: [
        ['label', 'string', "''", 'Text shown above the track, left-aligned'],
        ['value', 'number', '—', 'Controlled value (required)'],
        ['min', 'number', '0', 'Lower bound'],
        ['max', 'number', '100', 'Upper bound'],
        ['step', 'number', '1', 'Increment'],
        ['onChange', 'func', '—', 'Receives the new value as a number'],
        ['formatValue', 'func', '—', 'Formats the displayed value, e.g. (v) => `${v}%`'],
        ['showValue', 'bool', 'true', 'Show the value on the right of the header'],
        ['disabled', 'bool', 'false', 'Disabled state'],
        ['id', 'string', 'auto', 'Input id — generated via useId when omitted'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
    {
      title: 'RangeSlider',
      component: RangeSlider,
      componentName: 'RangeSlider',
      rows: [
        ['label', 'string', "''", 'Text above the track; names the group for assistive tech'],
        ['value', '[number, number]', '—', 'Controlled pair [low, high] (required); out-of-bounds values are clamped for display'],
        ['min', 'number', '0', 'Lower bound'],
        ['max', 'number', '100', 'Upper bound'],
        ['step', 'number', '1', 'Increment'],
        ['minGap', 'number', '0', 'Smallest distance the thumbs keep — they never cross'],
        ['onChange', 'func', '—', 'Every move — receives [low, high] (live display)'],
        ['onChangeEnd', 'func', '—', 'Once a thumb is released (pointer up / key up) — commit expensive work here'],
        ['formatValue', 'func', '—', 'Formats each value in the header ("low – high") and aria-valuetext'],
        ['showValue', 'bool', 'true', 'Show "low – high" on the right of the header'],
        ['minLabel', 'string', "'Minimum'", 'Accessible name of the low thumb'],
        ['maxLabel', 'string', "'Maximum'", 'Accessible name of the high thumb'],
        ['disabled', 'bool', 'false', 'Disabled state'],
        ['className', 'string', "''", 'Additional CSS class'],
      ],
    },
  ],
  propsNote:
    'The filled portion is driven by an inline --ds-slider-fill percentage: a gradient on the WebKit track, ::-moz-range-progress on Gecko. Track height and thumb size are component-scoped custom properties (--ds-slider-track-height, --ds-slider-thumb-size) — override those rather than restyling the pseudo-elements. The thumb only reacts to hover on pointer devices. RangeSlider reuses the same thumb rules; its fill between the thumbs is --ds-range-from / --ds-range-to.',
  usage: [
    {
      title: 'Logo size on the CLS-DK logo generator',
      code: `import Slider from '@ajat/design-system/Slider';

const [logoSize, setLogoSize] = useState(70);

<Slider
  label="Logo størrelse"
  value={logoSize}
  min={50}
  max={95}
  step={5}
  onChange={setLogoSize}
  formatValue={(v) => \`\${v}%\`}
/>`,
    },
    {
      title: 'A price range — commit on release',
      code: `import { RangeSlider } from '@ajat/design-system/Slider';

const [range, setRange] = useState([200, 600]);

<RangeSlider
  label="Price"
  value={range}
  min={0}
  max={1000}
  step={10}
  onChange={setRange}                 // live: moves the thumbs and the header
  onChangeEnd={(r) => applyPrice(r)}  // once released: re-filter
  formatValue={(v) => \`\${v} kr\`}
  minLabel="Price, minimum"
  maxLabel="Price, maximum"
/>`,
    },
  ],
};

export default function SliderPage() {
  const [size, setSize] = useState(70);
  const [volume, setVolume] = useState(0);
  const [opacity, setOpacity] = useState(100);
  const [temp, setTemp] = useState(18);
  const [price, setPrice] = useState([200, 600]);
  const [committed, setCommitted] = useState([200, 600]);
  const [age, setAge] = useState([18, 30]);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States">
        <div
          className="max-w-[480px]"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-l)' }}
        >
          <Slider
            label="Logo størrelse"
            value={size}
            min={50}
            max={95}
            step={5}
            onChange={setSize}
            formatValue={(v) => `${v}%`}
          />
          <Slider label="At minimum" value={volume} min={0} max={10} onChange={setVolume} />
          <Slider
            label="At maximum"
            value={opacity}
            min={0}
            max={100}
            onChange={setOpacity}
            formatValue={(v) => `${v}%`}
          />
          <Slider
            label="Custom format"
            value={temp}
            min={10}
            max={30}
            onChange={setTemp}
            formatValue={(v) => `${v} °C`}
          />
          <Slider
            label="Without value"
            value={size}
            min={50}
            max={95}
            step={5}
            onChange={setSize}
            showValue={false}
          />
          <Slider
            label="Disabled"
            value={40}
            min={0}
            max={100}
            onChange={() => {}}
            formatValue={(v) => `${v}%`}
            disabled
          />
        </div>
      </DemoCard>

      <DemoCard title="RangeSlider" hint="two thumbs — onChange live, onChangeEnd on release">
        <div
          className="max-w-[480px]"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-l)' }}
        >
          <RangeSlider
            label="Price"
            value={price}
            min={0}
            max={1000}
            step={10}
            onChange={setPrice}
            onChangeEnd={setCommitted}
            formatValue={(v) => `${v} kr`}
            minLabel="Price, minimum"
            maxLabel="Price, maximum"
          />
          <p className="typo-body-sm" style={{ margin: 0, color: 'var(--color-gray-6)' }}>
            Committed on release: {committed[0]} kr – {committed[1]} kr
          </p>
          <RangeSlider label="Age, minGap 2" value={age} min={16} max={65} minGap={2} onChange={setAge} />
          <RangeSlider label="Disabled" value={[20, 80]} onChange={() => {}} disabled />
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
